import {t, tp, tpl} from '../../i18n/i18n.js';
import {inferLayoutContexts, isLayoutEnabled} from '../../core/page/page.core.js';
import {
    MAX_CAROUSEL_POSITION,
    PREVIEW_PAGE_PATH,
    carouselOrder,
    carouselPosition,
    carouselSlot,
    fixedColumnCount,
    layoutMapModel,
    nextCarouselPosition,
    wrapGridPosition,
} from '../../core/page/layout.core.js';
import {
    DEFAULT_LAYOUT_CONTEXT,
    GRID_BASE_KEYS,
    GRID_MAX_COLUMNS,
    GridKey,
    LayoutPresentation,
    PIN_POSITION_KEY,
    gridFieldKey,
    isTruthyFlag,
} from '../../core/vocabulary.js';

const LAYOUT_PREVIEW_DEBOUNCE_MS = 160;
const GRID_FIELD_HINTS = {
    [GridKey.COLUMN]: { min: 1, max: GRID_MAX_COLUMNS, hintKey: 'pageForm.gridHintColumn' },
    [GridKey.WIDTH]: { min: 1, max: GRID_MAX_COLUMNS, hintKey: 'pageForm.gridHintWidth' },
    [GridKey.ROW]: { min: 1, hintKey: 'pageForm.gridHintRow' },
    [GridKey.HEIGHT]: { min: 1, hintKey: 'pageForm.gridHintHeight' },
};
const COLUMN_BOUND_KEYS = [GridKey.COLUMN, GridKey.WIDTH];
const LEGEND = [
    ['swatch-existing', 'pageForm.legendExisting',    'pageForm.legendExistingHelp'],
    ['swatch-current',  'pageForm.legendCurrent',     'pageForm.legendCurrentHelp'],
    ['swatch-pinned',   'pageForm.legendPinned',      'pageForm.legendPinnedHelp'],
    ['swatch-staged',   'pageForm.legendStaged',      'pageForm.legendStagedHelp'],
    ['swatch-moved',    'pageForm.legendAutoShifted', 'pageForm.legendAutoShiftedHelp'],
    ['swatch-conflict', 'pageForm.legendConflict',    'pageForm.legendConflictHelp'],
];

/**
 * @typedef {Object} LayoutPlacementState
 * @property {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @property {import('../../shell/types/user.types.js').TemplateLayoutConfig | null} layoutConfig
 * @property {string} pagePath
 * @property {string} pageTitle
 * @property {boolean} editMode
 * @property {string} templateName
 */

/**
 * @typedef {Object} GridFieldHint
 * @property {string} baseKey
 * @property {number} min
 * @property {number} [max]
 * @property {string} hintKey
 */

/**
 * Where the page appears in its template's layout contexts: the grid position fields and
 * their "position" card, the layout map (or carousel order list) above them, and the
 * debounced `layout-preview` requests that let the server resolve overlaps. The page form
 * renders the field controls; this panel wires the grid ones and groups them per context.
 */
export class LayoutPlacement {
    #root;
    #emit;
    #getState;
    #onPositionChange;
    #onMapsRendered;
    /** @type {Map<string, import('../../shell/types/user.types.js').TemplateLayoutEntry[]>} */
    #entriesByContext = new Map();
    /** @type {Map<string, import('../../shell/types/user.types.js').LayoutPreviewResponse | null>} */
    #previewByContext = new Map();
    #previewDebounce = null;
    /** @type {Map<string, {path: string, row: number, column: number, width: number, height: number} | null>} */
    #lastRequestByContext = new Map();
    /** @type {Map<string, boolean>} */
    #touchedByContext = new Map();
    /** @type {Map<string, HTMLElement>} */
    #gridCards = new Map();
    /** @type {Map<string, HTMLElement>} */
    #toggleGroups = new Map();
    /** @type {Map<string, string>} */
    #toggleKeyToContext = new Map();

    /**
     * @param {{
     *   root: ShadowRoot,
     *   emit: (name: string, detail: Object) => void,
     *   getState: () => LayoutPlacementState,
     *   onPositionChange: () => void,
     *   onMapsRendered?: () => void,
     * }} options `onPositionChange` runs when the user picks a carousel position, `onMapsRendered`
     *     each time the maps are drawn again, so after every new preview
     */
    constructor({root, emit, getState, onPositionChange, onMapsRendered}) {
        this.#root = root;
        this.#emit = emit;
        this.#getState = getState;
        this.#onPositionChange = onPositionChange;
        this.#onMapsRendered = onMapsRendered;
    }

    /**
     * Whether the server preview of a shown context has the page overlapping a pinned page. A
     * publication refuses that placement, like the page list, which blocks publishing the draft.
     * @returns {boolean}
     */
    overlapsPinnedPage() {
        const currentPath = this.#getState().pagePath || PREVIEW_PAGE_PATH;
        return this.contexts()
            .filter((context) => this.#hasGridInputs(context) && this.#isEnabled(context))
            .some((context) => {
                const hardConflicts = this.#previewByContext.get(context)?.hardConflicts;
                return Array.isArray(hardConflicts) && hardConflicts.includes(currentPath);
            });
    }

    #$(selector) {
        return this.#root.querySelector(selector);
    }

    #input(base, context) {
        return this.#$(`[name="${gridFieldKey(context, base)}"]`);
    }

    /** @returns {string[]} */
    contexts() {
        const {fields, layoutConfig} = this.#getState();
        return inferLayoutContexts(fields, layoutConfig);
    }

    setEntries(context, entries) {
        const normalizedContext = context || DEFAULT_LAYOUT_CONTEXT;
        this.#entriesByContext.set(normalizedContext, Array.isArray(entries) ? entries : []);
        if (this.#isCarousel(normalizedContext) && !this.#getState().editMode
            && this.#isEnabled(normalizedContext)
            && !this.#touchedByContext.get(normalizedContext)) {
            this.#appendCarouselPosition(normalizedContext);
        }
        this.renderMaps();
    }

    setPreview(context, preview) {
        const normalizedContext = context || DEFAULT_LAYOUT_CONTEXT;
        this.#previewByContext.set(normalizedContext, preview ?? null);
        this.renderMaps();
    }

    resetData() {
        this.#entriesByContext = new Map();
        this.clearPreviewState();
        this.renderMaps();
    }

    clearPreviewState() {
        this.#previewByContext = new Map();
        this.#lastRequestByContext = new Map();
    }

    clearPositionTouched() {
        this.#touchedByContext = new Map();
    }

    /** Cancels a pending layout preview request. */
    dispose() {
        if (this.#previewDebounce) {
            clearTimeout(this.#previewDebounce);
            this.#previewDebounce = null;
        }
    }

    /**
     * Layout context whose grid position a field holds, or null for other fields.
     * @param {string} fieldKey
     * @returns {string | null}
     */
    gridFieldContext(fieldKey) {
        for (const context of this.contexts()) {
            if (GRID_BASE_KEYS.includes(fieldKey) && context === DEFAULT_LAYOUT_CONTEXT) {
                return DEFAULT_LAYOUT_CONTEXT;
            }
            if (GRID_BASE_KEYS.some((name) => fieldKey === gridFieldKey(context, name))) {
                return context;
            }
        }
        return null;
    }

    /**
     * Input limits of a grid number field. A bare grid key (`row`) gets the default context's
     * limits even when no layout context is inferred.
     * @param {string} fieldKey
     * @returns {GridFieldHint | null}
     */
    gridHint(fieldKey) {
        const context = this.gridFieldContext(fieldKey) || DEFAULT_LAYOUT_CONTEXT;
        const baseKey = GRID_BASE_KEYS.find((key) => fieldKey === gridFieldKey(context, key));
        const hint = baseKey ? this.#gridFieldHint(baseKey, context) : null;
        return hint ? {...hint, baseKey} : null;
    }

    #toggleFieldFor(context = DEFAULT_LAYOUT_CONTEXT) {
        return this.#getState().layoutConfig?.contexts?.[context]?.enabledByField || '';
    }

    /**
     * Field key of the checkbox that switches a context on, keyed by context.
     * @returns {Map<string, string>}
     */
    toggleFieldsByContext() {
        const toggles = new Map();
        for (const context of this.contexts()) {
            const toggleKey = this.#toggleFieldFor(context);
            if (toggleKey) toggles.set(context, toggleKey);
        }
        return toggles;
    }

    #isCarousel(context) {
        return this.#getState().layoutConfig?.contexts?.[context]?.presentation === LayoutPresentation.CAROUSEL;
    }

    /** The fixed column count of a grid context, or null when its columns are unbounded. */
    #columnCount(context) {
        return fixedColumnCount(this.#getState().layoutConfig?.contexts?.[context], this.#isCarousel(context));
    }

    #gridFieldHint(name, context) {
        const hint = GRID_FIELD_HINTS[name];
        const columns = this.#columnCount(context);
        return hint && columns && COLUMN_BOUND_KEYS.includes(name) ? {...hint, max: columns} : hint;
    }

    /** Wraps a saved position beyond a fixed column count onto the next rows, as the backend does. */
    wrapGridInputsToColumns() {
        for (const context of this.contexts()) {
            const columns = this.#columnCount(context);
            if (!columns) continue;
            const row = this.#input(GridKey.ROW, context);
            const column = this.#input(GridKey.COLUMN, context);
            if (!row || !column) continue;
            const wrapped = wrapGridPosition(Number(row.value), Number(column.value), columns);
            if (!wrapped) continue;
            row.value = String(wrapped.row);
            column.value = String(wrapped.column);
        }
    }

    #appendCarouselPosition(context) {
        this.#writeCarouselPosition(context, nextCarouselPosition(this.#entriesByContext.get(context), this.#getState().pagePath));
    }

    #writeCarouselPosition(context, position) {
        const row = this.#input(GridKey.ROW, context);
        const column = this.#input(GridKey.COLUMN, context);
        if (!row || !column) return;
        const slot = carouselSlot(position);
        row.value = String(slot.row);
        column.value = String(slot.column);
        for (const key of [GridKey.WIDTH, GridKey.HEIGHT]) {
            const input = this.#input(key, context);
            if (input) input.value = '1';
        }
    }

    #setCarouselPosition(context, position) {
        this.#writeCarouselPosition(context, position);
        this.#touchedByContext.set(context, true);
        this.#previewByContext.set(context, null);
        this.#onPositionChange();
        this.renderMaps();
        this.requestPreview();
    }

    /** Fills empty grid inputs of an enabled context with 1 while creating a page. */
    applyDefaultGridPlacement(context = DEFAULT_LAYOUT_CONTEXT) {
        if (this.#getState().editMode || !this.#isEnabled(context)) {
            return;
        }
        for (const key of GRID_BASE_KEYS) {
            const input = this.#input(key, context);
            if (input && !input.value.trim()) {
                input.value = '1';
            }
        }
    }

    #hasGridInputs(context = DEFAULT_LAYOUT_CONTEXT) {
        const keys = new Set(this.#getState().fields.map((field) => field.key));
        return keys.has(gridFieldKey(context, GridKey.ROW)) && keys.has(gridFieldKey(context, GridKey.COLUMN));
    }

    #isEnabled(context = DEFAULT_LAYOUT_CONTEXT) {
        const {layoutConfig, fields} = this.#getState();
        return isLayoutEnabled(layoutConfig, context, this.#root, fields);
    }

    #clearGridInputs(context = DEFAULT_LAYOUT_CONTEXT) {
        for (const key of GRID_BASE_KEYS) {
            const input = this.#input(key, context);
            if (input) {
                input.value = '';
            }
        }
    }

    /** Hides the grid fields of disabled contexts and collapses their cards. */
    syncGridFieldVisibility() {
        for (const context of this.contexts()) {
            const layoutEnabled = this.#isEnabled(context);
            for (const key of GRID_BASE_KEYS) {
                const group = this.#$(`.field-group[data-field-key="${gridFieldKey(context, key)}"]`);
                if (group) {
                    group.hidden = !layoutEnabled;
                }
            }
            // The context card collapses rather than hiding, so its body animates closed via
            // the CSS grid-template-rows transition and the layout map and position panel
            // ease in and out of view.
            const contextCard = this.#$(`.layout-context-card[data-context="${context}"]`);
            if (contextCard) contextCard.classList.toggle('is-collapsed', !layoutEnabled);
        }
    }

    #readGridValue(name, context = DEFAULT_LAYOUT_CONTEXT) {
        const input = this.#input(name, context);
        if (!input) return null;
        const raw = String(input.value ?? '').trim();
        if (!raw) return null;
        const parsed = Number.parseInt(raw, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    #readPinValue() {
        const pinInput = this.#$(`[name="${PIN_POSITION_KEY}"]`);
        if (!pinInput) return false;
        if (pinInput.type === 'checkbox') {
            return Boolean(pinInput.checked);
        }
        return isTruthyFlag(pinInput.value);
    }

    /**
     * Wires a context's toggle checkbox: switching it off clears the position, switching it
     * on places the page at a default position.
     * @param {HTMLInputElement} input
     * @param {string} fieldKey
     */
    bindToggle(input, fieldKey) {
        const toggleContext = this.contexts().find((context) => this.#toggleFieldFor(context) === fieldKey);
        if (!toggleContext) return;
        input.addEventListener('change', () => {
            if (!input.checked) {
                this.#clearGridInputs(toggleContext);
            } else {
                this.applyDefaultGridPlacement(toggleContext);
                if (this.#isCarousel(toggleContext)) {
                    this.#appendCarouselPosition(toggleContext);
                }
            }
            this.#previewByContext.set(toggleContext, null);
            this.#lastRequestByContext.delete(toggleContext);
            this.#touchedByContext.set(toggleContext, false);
            this.syncGridFieldVisibility();
            this.renderMaps();
            this.requestPreview();
        });
    }

    /** Starts collecting the grid and toggle field groups of a new field render. */
    beginFieldPlacement() {
        this.#gridCards = new Map();
        this.#toggleGroups = new Map();
        this.#toggleKeyToContext = new Map([...this.toggleFieldsByContext()].map(([context, key]) => [key, context]));
    }

    /**
     * Takes a rendered field group into its context card when it is a grid or toggle field,
     * wiring grid inputs to validate their range and refresh the layout preview.
     * @param {import('../../shell/types/user.types.js').TemplateField} field
     * @param {HTMLElement} group
     * @param {HTMLElement} input
     * @returns {boolean} false when the field belongs in the regular form flow
     */
    adoptFieldGroup(field, group, input) {
        const gridContext = this.gridFieldContext(field.key);
        if (gridContext) {
            this.applyDefaultGridPlacement(gridContext);
            this.#bindGridInput(field, group, input, gridContext);
            if (!this.#gridCards.has(gridContext)) {
                // Wrapped in its layout-context-card by appendContextCards.
                this.#gridCards.set(gridContext, this.#createGridCard(gridContext));
            }
            this.#gridCards.get(gridContext).querySelector('.grid-card-fields').appendChild(group);
            return true;
        }
        const toggleContext = this.#toggleKeyToContext.get(field.key);
        if (toggleContext) {
            // Toggle fields head their context card instead of flowing with the content.
            this.#toggleGroups.set(toggleContext, group);
            return true;
        }
        return false;
    }

    #bindGridInput(field, group, input, gridContext) {
        input.addEventListener('input', () => {
            this.#touchedByContext.set(gridContext, true);
            this.#previewByContext.set(gridContext, null);
            const gridBaseKey = GRID_BASE_KEYS.find((k) => field.key === gridFieldKey(gridContext, k));
            const hintMeta = gridBaseKey ? this.#gridFieldHint(gridBaseKey, gridContext) : null;
            if (hintMeta) {
                const raw = input.value.trim();
                const parsed = Number.parseInt(raw, 10);
                const outOfRange = raw.length > 0 && (!Number.isFinite(parsed) || parsed < hintMeta.min || (hintMeta.max != null && parsed > hintMeta.max));
                input.classList.toggle('is-invalid', outOfRange);
                let errorEl = group.querySelector('.field-error');
                if (outOfRange) {
                    if (!errorEl) {
                        errorEl = document.createElement('span');
                        errorEl.className = 'field-error';
                        group.appendChild(errorEl);
                    }
                    errorEl.textContent = t('pageForm.gridValueInvalid');
                } else if (errorEl) {
                    errorEl.remove();
                }
            }
            this.renderMaps();
            this.requestPreview();
        });
    }

    #createGridCard(context) {
        const gridCard = document.createElement('div');
        gridCard.className = 'grid-fields-card';
        gridCard.dataset.context = context;
        const gridCardHeader = document.createElement('div');
        gridCardHeader.className = 'grid-card-header';
        const gridCardIcon = document.createElement('i');
        gridCardIcon.className = 'fa-solid fa-table-cells-large';
        gridCardHeader.appendChild(gridCardIcon);
        gridCardHeader.appendChild(document.createTextNode(` ${t('pageForm.positionTitle')}`));
        gridCard.appendChild(gridCardHeader);
        const gridCardFields = document.createElement('div');
        gridCardFields.className = 'grid-card-fields';
        gridCard.appendChild(gridCardFields);
        return gridCard;
    }

    /**
     * Appends one layout-context card per context that has grid or toggle fields, then
     * applies default positions and the enabled state.
     * @param {HTMLElement} container
     */
    appendContextCards(container) {
        const {layoutConfig} = this.#getState();
        for (const context of this.contexts()) {
            if (!this.#gridCards.has(context) && !this.#toggleGroups.has(context)) {
                continue;
            }
            const contextCard = document.createElement('div');
            contextCard.className = 'layout-context-card';
            contextCard.dataset.context = context;
            contextCard.dataset.presentation = layoutConfig?.contexts?.[context]?.presentation || LayoutPresentation.GRID;

            // Header row: the toggle field, styled as a settings strip.
            if (this.#toggleGroups.has(context)) {
                const header = document.createElement('div');
                header.className = 'layout-context-card-header';
                header.appendChild(this.#toggleGroups.get(context));
                contextCard.appendChild(header);
            }

            // Body: layout map and position card. Collapses with a height transition when the
            // toggle is off and stays in the DOM, so opening and closing both animate.
            const body = document.createElement('div');
            body.className = 'layout-context-card-body';
            const bodyInner = document.createElement('div');
            bodyInner.className = 'layout-context-card-body-inner';
            if (this.#gridCards.has(context)) {
                this.#gridCards.get(context).hidden = this.#isCarousel(context);
                bodyInner.appendChild(this.#gridCards.get(context));
            }
            body.appendChild(bodyInner);
            contextCard.appendChild(body);

            container.appendChild(contextCard);
        }
        for (const context of this.contexts()) {
            this.applyDefaultGridPlacement(context);
        }
        this.syncGridFieldVisibility();
    }

    #contextLabel(context) {
        return context === DEFAULT_LAYOUT_CONTEXT
            ? t('pageForm.layoutMap')
            : `${t('pageForm.layoutMap')} (${context})`;
    }

    /** Re-renders the layout map or carousel order list of every enabled context. */
    renderMaps() {
        this.#renderMapSections();
        this.#onMapsRendered?.();
    }

    #renderMapSections() {
        const container = this.#$('#fields-container');
        if (!container) {
            return;
        }

        const visibleContexts = this.contexts().filter((context) =>
            this.#hasGridInputs(context) && this.#isEnabled(context)
        );

        container.querySelectorAll('.layout-map-section, .carousel-order-section').forEach((section) => section.remove());
        if (visibleContexts.length === 0) {
            return;
        }

        for (const context of visibleContexts) {
            const section = this.#isCarousel(context)
                ? this.#createCarouselOrderSection(context)
                : this.#createLayoutMapSection(context);
            const gridCard = this.#$(`.grid-fields-card[data-context="${context}"]`);
            if (gridCard) {
                gridCard.insertAdjacentElement('beforebegin', section);
            } else {
                container.appendChild(section);
            }
        }
    }

    #createCarouselOrderSection(context) {
        const {pagePath, pageTitle} = this.#getState();
        const section = document.createElement('section');
        section.className = 'carousel-order-section';
        section.dataset.context = context;

        const heading = document.createElement('h4');
        heading.textContent = t('pageForm.carouselOrderTitle');
        const hint = document.createElement('p');
        hint.textContent = t('pageForm.carouselOrderHint');

        const label = document.createElement('label');
        const inputId = `carousel-position-${context}`;
        label.setAttribute('for', inputId);
        label.textContent = t('pageForm.carouselPosition');
        const input = document.createElement('input');
        input.id = inputId;
        input.className = 'carousel-position-input';
        input.type = 'number';
        input.min = '1';
        input.max = String(MAX_CAROUSEL_POSITION);
        input.step = '1';
        const row = this.#readGridValue(GridKey.ROW, context);
        const column = this.#readGridValue(GridKey.COLUMN, context);
        if (row && column) input.value = String(carouselPosition(row, column));
        input.addEventListener('change', () => {
            if (!input.reportValidity() || !input.value) return;
            this.#setCarouselPosition(context, Number(input.value));
        });

        const list = document.createElement('ol');
        list.className = 'carousel-order-list';
        list.setAttribute('aria-label', heading.textContent);
        const currentPath = pagePath || PREVIEW_PAGE_PATH;
        const slides = carouselOrder({
            entries: this.#entriesByContext.get(context),
            preview: this.#previewByContext.get(context),
            currentPath,
            currentTitle: pageTitle || t('pageForm.legendCurrent'),
            row,
            column,
        });
        for (const entry of slides) {
            const item = document.createElement('li');
            item.className = 'carousel-order-item';
            const number = document.createElement('span');
            number.className = 'carousel-order-number';
            number.textContent = String(carouselPosition(entry.row, entry.column));
            const title = document.createElement('span');
            title.className = 'carousel-order-name';
            title.textContent = entry.title || entry.path.split('/').pop();
            item.append(number, title);
            if (entry.path === currentPath) {
                item.classList.add('is-current');
                item.setAttribute('aria-current', 'true');
                const badge = document.createElement('span');
                badge.className = 'carousel-order-current';
                badge.textContent = t('pageForm.legendCurrent');
                item.appendChild(badge);
            }
            list.appendChild(item);
        }
        section.append(heading, hint, label, input, list);
        return section;
    }

    #createLayoutMapSection(context) {
        const section = document.createElement('div');
        section.className = 'layout-map-section';
        section.dataset.context = context;

        const header = document.createElement('div');
        header.className = 'layout-map-header';
        const title = document.createElement('h4');
        title.textContent = this.#contextLabel(context);
        header.appendChild(title);

        const status = document.createElement('p');
        status.className = 'layout-map-status';
        status.dataset.context = context;

        const grid = document.createElement('div');
        grid.className = 'layout-map-grid';
        grid.dataset.context = context;
        grid.setAttribute('aria-live', 'polite');

        const legend = document.createElement('p');
        legend.className = 'layout-map-legend';
        for (const [swatchClass, labelKey, helpKey] of LEGEND) {
            const item = document.createElement('span');
            item.className = 'legend-item';
            item.title = t(helpKey);
            const swatch = document.createElement('span');
            swatch.className = `legend-swatch ${swatchClass}`;
            const label = document.createElement('span');
            label.textContent = t(labelKey);
            item.appendChild(swatch);
            item.appendChild(document.createTextNode(' '));
            item.appendChild(label);
            legend.appendChild(item);
        }

        section.appendChild(header);
        section.appendChild(status);
        section.appendChild(grid);
        section.appendChild(legend);

        this.#renderLayoutMap(context, status, grid);
        return section;
    }

    #renderLayoutMap(context, status, grid) {
        const currentPath = this.#getState().pagePath || PREVIEW_PAGE_PATH;
        const currentRow = this.#readGridValue(GridKey.ROW, context);
        const currentColumn = this.#readGridValue(GridKey.COLUMN, context);
        const typed = currentRow && currentColumn
            ? {
                path: currentPath,
                title: 'Current',
                row: currentRow,
                column: currentColumn,
                width: this.#readGridValue(GridKey.WIDTH, context) ?? 1,
                height: this.#readGridValue(GridKey.HEIGHT, context) ?? 1,
                pinned: this.#readPinValue()
            }
            : null;

        const model = layoutMapModel({
            entries: this.#entriesByContext.get(context) || [],
            preview: this.#previewByContext.get(context) ?? null,
            currentPath,
            typed,
            lastRequest: this.#lastRequestByContext.get(context),
            fixedColumns: this.#columnCount(context),
        });
        const current = model.current;
        if (model.applyRelocation) {
            const rowInput = this.#input(GridKey.ROW, context);
            const colInput = this.#input(GridKey.COLUMN, context);
            if (rowInput) rowInput.value = String(current.row);
            if (colInput) colInput.value = String(current.column);
        }

        grid.style.setProperty('--layout-cols', String(model.columns));
        grid.style.setProperty('--layout-rows', String(model.rows));
        grid.innerHTML = '';

        for (let row = 1; row <= model.rows; row++) {
            for (let col = 1; col <= model.columns; col++) {
                const cell = document.createElement('div');
                cell.className = 'layout-map-cell';
                cell.dataset.row = String(row);
                cell.dataset.column = String(col);
                grid.appendChild(cell);
            }
        }

        for (const placement of model.existing) {
            if (!model.fits(placement)) {
                continue;
            }
            const block = document.createElement('div');
            const conflictClass = model.conflictPaths.has(placement.path) ? ' conflict' : '';
            const movedClass = model.movedPaths.has(placement.path) ? ' moved' : '';
            const pinnedClass = placement.staged ? ' staged' : (placement.pinned ? ' pinned' : '');
            const thumbnailClass = placement.thumbnailUrl ? ' has-thumbnail' : '';
            block.className = `layout-map-block${conflictClass}${movedClass}${pinnedClass}${thumbnailClass}`;
            block.style.gridColumn = `${placement.column} / span ${placement.width}`;
            block.style.gridRow = `${placement.row} / span ${placement.height}`;
            block.title = placement.path;
            if (placement.thumbnailUrl) {
                block.style.setProperty('--thumbnail-url', `url("${placement.thumbnailUrl.replace(/"/g, '\\"')}")`);
            }
            block.textContent = placement.title || placement.path.split('/').pop();
            grid.appendChild(block);
        }

        if (current && model.fits(current)) {
            const element = document.createElement('div');
            const currentConflict = model.conflictPaths.has(current.path) || model.localConflictPaths.length > 0;
            const currentPinned = Boolean(current.pinned);
            element.className = `layout-map-current${currentConflict ? ' conflict' : ''}${model.currentWasRelocated ? ' moved' : ''}${currentPinned ? ' pinned' : ''}`;
            element.style.gridColumn = `${current.column} / span ${current.width}`;
            element.style.gridRow = `${current.row} / span ${current.height}`;
            element.textContent = t('pageForm.legendCurrent');
            grid.appendChild(element);
        }

        status.textContent = this.#statusText(model);
    }

    /** @param {import('../../core/page/layout.core.js').LayoutMapModel} model */
    #statusText(model) {
        const {current, hardConflictPaths, localConflictPaths, otherMovedPaths, currentWasRelocated} = model;
        if (!current) {
            return t('pageForm.layoutSetPosition');
        }
        if (model.currentOutsideMap) {
            let suffix = '';
            if (hardConflictPaths.size > 0) {
                suffix = ` ${tp('pageForm.layoutPinnedOverlap', hardConflictPaths.size)}`;
            } else if (localConflictPaths.length > 0) {
                suffix = ` ${tp('pageForm.layoutOverlap', localConflictPaths.length)}`;
            }
            return tpl('pageForm.layoutOutsideRange', {cols: model.columns, rows: model.rows}) + suffix;
        }
        if (hardConflictPaths.size > 0) {
            return tp('pageForm.layoutPinnedOverlap', hardConflictPaths.size);
        }
        if (currentWasRelocated || otherMovedPaths.size > 0) {
            const shifted = otherMovedPaths.size + (currentWasRelocated ? 1 : 0);
            const resolvedPosition = currentWasRelocated
                ? ' ' + tpl('pageForm.layoutAutoShiftedPosition', {row: current.row, column: current.column})
                : '';
            return tp('pageForm.layoutAutoShifted', shifted) + resolvedPosition;
        }
        if (localConflictPaths.length === 0) {
            return t('pageForm.layoutNoOverlap') + '.';
        }
        return tp('pageForm.layoutOverlap', localConflictPaths.length);
    }

    /** Asks the server, after a short debounce, where every enabled context would place the page. */
    requestPreview() {
        if (this.#previewDebounce) {
            clearTimeout(this.#previewDebounce);
        }
        this.#previewDebounce = setTimeout(() => {
            this.#previewDebounce = null;
            this.#emitPreviewForVisibleContexts();
        }, LAYOUT_PREVIEW_DEBOUNCE_MS);
    }

    #emitPreviewForVisibleContexts() {
        for (const context of this.contexts()) {
            if (!this.#hasGridInputs(context) || !this.#isEnabled(context)) {
                this.#previewByContext.delete(context);
                this.#lastRequestByContext.delete(context);
                continue;
            }
            this.#emitPreview(context);
        }
        this.renderMaps();
    }

    #emitPreview(context) {
        const {pagePath, templateName, fields} = this.#getState();
        const row = this.#readGridValue(GridKey.ROW, context);
        const column = this.#readGridValue(GridKey.COLUMN, context);
        if (!row || !column) {
            this.#previewByContext.set(context, null);
            this.#lastRequestByContext.delete(context);
            return;
        }
        this.#lastRequestByContext.set(context, {
            path: pagePath || PREVIEW_PAGE_PATH,
            row,
            column,
            width: this.#readGridValue(GridKey.WIDTH, context) ?? 1,
            height: this.#readGridValue(GridKey.HEIGHT, context) ?? 1
        });
        this.#emit('layout-preview', {
            templateName,
            pagePath: pagePath || null,
            context,
            formElement: this.#$('#page-form'),
            fields,
            prioritizePosition: Boolean(this.#touchedByContext.get(context)),
        });
    }
}
